// ensure-security-scanners-installed.ts
//
// Cài + detect 2 security scanner cho pull-request-checker: gitleaks (secret) +
// trivy (CVE). Cài MẶC ĐỊNH qua brew lúc init/sync, fail-soft.
//
// Khác ensure-gitnexus (npm + auto-update mỗi sync):
//   - Dùng brew (scanner phân phối qua brew/apt, không npm).
//   - CHỈ cài nếu THIẾU — KHÔNG auto-update mỗi sync (scanner version ít đổi
//     hành vi; `brew upgrade` chậm + đôi khi kéo cả dependency khác).
//   - brew thiếu (Linux/CI) → fail-soft: skip, không chặn init.
//
// Pattern detect: which/where probe (giống detect-gitnexus), không shell:true.

import { spawnSync } from "node:child_process";
import { detectHostPlatform } from "./detect-host-platform.js";
import { log } from "./terminal-logger.js";

const SCANNERS = ["gitleaks", "trivy"] as const;
type Scanner = (typeof SCANNERS)[number];

const BREW_INSTALL_TIMEOUT_MS = 5 * 60 * 1000;
const PROBE_TIMEOUT_MS = 5000;

export interface ScannerStatus {
  name: Scanner;
  installed: boolean;
}

// Binary có trong PATH không (which/where — external, không shell:true).
function isInstalled(name: Scanner): boolean {
  const isWin = detectHostPlatform() === "win32";
  const r = spawnSync(isWin ? "where" : "which", [name], {
    encoding: "utf8",
    timeout: PROBE_TIMEOUT_MS,
  });
  return !r.error && r.status === 0 && (r.stdout || "").trim().length > 0;
}

// Export cho doctor: doctor phải BIẾT có brew hay không mới chọn được câu gợi ý cài
// đúng với máy (xem buildMissingScannerHint).
export function brewAvailable(): boolean {
  const r = spawnSync("brew", ["--version"], { encoding: "utf8", timeout: PROBE_TIMEOUT_MS });
  return !r.error && r.status === 0;
}

// Cài 1 lượt các scanner còn thiếu qua `brew install <a> <b>`. Trả true nếu chạy OK.
function brewInstall(missing: Scanner[]): boolean {
  log.info(`Cài security scanner qua brew: ${missing.join(", ")} (có thể mất 1-2 phút)...`);
  const r = spawnSync("brew", ["install", ...missing], {
    stdio: ["inherit", "inherit", "pipe"],
    timeout: BREW_INSTALL_TIMEOUT_MS,
    encoding: "utf8",
  });
  if (r.status !== 0) {
    if (r.stderr) process.stderr.write(r.stderr);
    return false;
  }
  return true;
}

// Đảm bảo gitleaks + trivy đã cài (cài cái thiếu qua brew). KHÔNG hỏi, fail-soft.
// Trả trạng thái từng scanner để caller (doctor) hiển thị.
export function ensureSecurityScannersInstalled(): ScannerStatus[] {
  const missing = SCANNERS.filter((s) => !isInstalled(s));

  if (missing.length === 0) {
    return SCANNERS.map((name) => ({ name, installed: true }));
  }

  if (!brewAvailable()) {
    // Không có brew (Linux/CI/Windows) → fail-soft: báo cách cài tay, không chặn.
    log.warn(
      `Thiếu security scanner (${missing.join(", ")}) và không tìm thấy brew. ` +
        "Security gate sẽ tự skip tới khi cài. Cài tay: xem trivy.dev / github.com/gitleaks/gitleaks.",
    );
    return SCANNERS.map((name) => ({ name, installed: !missing.includes(name) }));
  }

  try {
    const ok = brewInstall(missing);
    if (!ok) {
      log.warn(
        `Cài scanner qua brew thất bại (${missing.join(", ")}). Security gate skip tới khi cài. ` +
          "Thử tay: brew install " +
          missing.join(" "),
      );
    } else {
      log.success(`Đã cài security scanner: ${missing.join(", ")}.`);
    }
  } catch (err) {
    log.warn(
      `Lỗi cài scanner (${(err as Error).message}). Bỏ qua — workspace vẫn dùng được (gate skip).`,
    );
  }

  // Re-probe để trả trạng thái thật sau cài.
  return SCANNERS.map((name) => ({ name, installed: isInstalled(name) }));
}

// Trang hướng dẫn cài tay từng scanner — dùng khi máy KHÔNG có brew.
const MANUAL_INSTALL_DOCS: Record<Scanner, string> = {
  gitleaks: "github.com/gitleaks/gitleaks",
  trivy: "trivy.dev",
};

/**
 * Câu gợi ý cho scanner còn THIẾU mà doctor in ra. Hàm THUẦN (không I/O) để test
 * được cả hai vế mà không phải mock spawn.
 *
 * ⚠ `brewPresent` là bắt buộc, KHÔNG mặc định true: máy không có brew (Linux/CI, hoặc
 * macOS chưa cài Homebrew) mà vẫn in "brew install X" thì người dùng copy về
 * `command not found` — cảnh báo không chữa được dạy người ta phớt lờ mọi ⚠ khác.
 * Chữ nhánh không-brew bám theo đường init (ensureSecurityScannersInstalled) cho khớp.
 */
export function buildMissingScannerHint(name: Scanner, brewPresent: boolean): string {
  const base = "chưa cài — security gate skip phần này";
  return brewPresent
    ? `${base} (cài: brew install ${name})`
    : `${base} (máy chưa có brew — cài tay: xem ${MANUAL_INSTALL_DOCS[name]})`;
}

// Cho doctor: chỉ detect, không cài.
export function detectSecurityScanners(): ScannerStatus[] {
  return SCANNERS.map((name) => ({ name, installed: isInstalled(name) }));
}
